import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { api, getErrorMessage } from '@/utils';
import { API_ENDPOINTS, buildApiUrl, QUERY_CONFIG } from '@/constants';

import type { CallInsight } from '@/types/insights';

interface CallInsightsResponse {
    answers: CallInsight[] | [];
    createdAt: Date | null;
    updatedAt: Date | null;
}

interface UseFetchCallInsightsProps {
    data: CallInsightsResponse;
    isLoading: boolean;
    isError: boolean;
    errorMessage: string | null;
    refetch: () => void;
}

/**
 * Hook for fetching call insights for a given call
 *
 * @param {number|null} callId - Call ID
 * @param {boolean} enabled - Whether to enable the query
 * @param {number|null} campaignId - Campaign ID passed as a query param so the
 *   server can route directly to the correct storage without an extra DB lookup
 * @returns {UseFetchCallAnswersProps}
 */
const useFetchCallInsights = (callId: number | null, enabled: boolean = true, campaignId?: number | null): UseFetchCallInsightsProps => {
    const { data, isLoading, isError, error, refetch } = useQuery<CallInsightsResponse, AxiosError>({
        queryKey: ['api', 'call', callId, 'insights'],
        queryFn: async () => {
            if (!callId) {
                return { answers: [], createdAt: null, updatedAt: null };
            }

            const url = buildApiUrl(
                API_ENDPOINTS.CALL_INSIGHTS(callId),
                campaignId ? { campaignId } : undefined
            );
            const response = await api.get(url);

            return response.data as CallInsightsResponse;
        },
        enabled: enabled && callId !== null,
        staleTime: QUERY_CONFIG.STALE_TIME,
        refetchOnWindowFocus: QUERY_CONFIG.REFETCH_ON_WINDOW_FOCUS,
        retry: 2,
    });

    const errorMessage = getErrorMessage(error);

    return {
        data: data ?? { answers: [], createdAt: null, updatedAt: null },
        isLoading,
        isError,
        errorMessage,
        refetch,
    };
};

export default useFetchCallInsights;
